import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  type ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  type OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import type { Drug } from '../../models/drug.model';
import { LinkRoles } from '../../models/link.model';
import { DrugService } from '../../services/drug.service';
import { LinkComponent } from '../link/link.component';

@Component({
  selector: 'app-drug-search',
  templateUrl: './drug-search.component.html',
  styleUrls: ['./drug-search.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, LinkComponent]
})
export class DrugSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef;
  @Input() placeholder: string = '';
  @Input() closeButtonText: string = '';
  @Input() cssClass: string = '';
  @Input() showCloseButton: boolean = false;
  @Input() autoFocus: boolean = false;
  @Output() drugSelected = new EventEmitter<Drug>();
  @Output() search = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  searchQuery: string = '';
  searchResults: Drug[] = [];
  showResults: boolean = false;
  searchBlurTimeout: number | null = null;
  readonly LinkRoles = LinkRoles;
  private readonly searchSubject$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  constructor(private readonly drugService: DrugService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchSubject$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(query => {
            if (query?.length >= 2) {
              return this.drugService.getDrugAutoComplete$(query, 10);
            }
            return of([]);
          })
        )
        .subscribe({
          next: results => {
            this.searchResults = results ?? [];
            this.showResults = results.length > 0;
            this.cdr.markForCheck();
          },
          error: err => {
            console.error('Drug autocomplete error:', err);
          }
        })
    );
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      this.searchInput?.nativeElement?.focus();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearSearchBlurTimeout();
  }

  onSearch(searchQuery: string): void {
    this.search.emit(searchQuery);
    this.showResults = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.searchSubject$.next(this.searchQuery);
  }

  onSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.searchSubject$.next(this.searchQuery);
  }

  onSearchBlur(): void {
    this.clearSearchBlurTimeout();
    // The timeout ensures the element and its click event handler are still there, long enough to handle the click event
    this.searchBlurTimeout = setTimeout(() => {
      this.showResults = false;
      this.cdr.markForCheck();
    }, 200) as any;
  }

  onSearchFocus(): void {
    if (this.searchResults.length > 0) {
      this.clearSearchBlurTimeout();
      this.showResults = true;
    }
  }

  onClose(): void {
    this.showResults = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.close.emit();
  }

  selectDrug(drug: Drug): void {
    this.drugSelected.emit(drug);
    this.showResults = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.searchSubject$.next(this.searchQuery);
  }

  trackByDrugId(index: number, drug: Drug): number {
    return drug.drugID;
  }

  private clearSearchBlurTimeout(): void {
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = null;
    }
  }
}
